import React, { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import SquarePaymentForm from './SquarePaymentForm';
import PharmacySelectStep from './PharmacySelectStep';
import { OTP_ENABLED } from './PhoneOtpGate';
import { apiService } from '@/services/api';
import { isHiddenMedication } from '@/data/hiddenMedications';
import { RecommendationGate, BenefitList, TrustBanner, MedThumb, MembershipNotice, ProviderReviewNotice } from './checkoutUi';
import ProductLearnMore from './ProductLearnMore';
import ManufacturerPharmacyBanner from './ManufacturerPharmacyBanner';
import { MANUFACTURER_PHARMACIES, resolveManufacturerPharmacy, describePharmacyDestination, isManufacturerPharmacySelected, PHARMACY_DESTINATION } from '@/data/manufacturerPharmacies';
import { resolveManufacturerPrice, manufacturerPriceCopy } from '@/data/manufacturerPricing';
import { dexaAddOnCents, dexaAddOnPayload, dexaOfferAvailable } from '@/data/dexaAddOn';
import { conciergeAddOnAvailable, conciergeAddOnCents, conciergeAddOnPayload } from '@/data/conciergeAddOn';
import { otoCardOnFile } from '@/data/conciergeOto';
import OtoCardSaveNotice from './OtoCardSaveNotice';
import { DexaUpsell, ConciergeUpsell, AddOnLineItems } from './CheckoutUpsells';

// Generic catalog-driven checkout for every non-GLP visit type. Mirrors
// GlpCatalogCheckoutStep but parameterized: the patient sees ONE recommended
// product and can "Modify your plan" to switch dosage/strength — sourced from the
// backend shop catalog (GET /catalog/medications?visitType=<catalogVisitType>).
// PRICE FOLLOWS THE SELECTED SKU (catalog price, cents): on pay we send
// selectedMedIds:[medId] so the BE prices from the catalog (charge == displayed)
// and create-visit-v2 builds the preference from that medId.
//
// Props:
//   catalogVisitType — which catalog visitType to fetch SKUs from (weightloss/
//     trt/mensSexualHealth/antiAging). May differ from the Beluga create-visit
//     visitType the parent form uses (e.g. WSH fetches mensSexualHealth,
//     Sermorelin fetches antiAging).
//   filter?(item) — optional predicate to narrow the SKU list (e.g. WSH → women's
//     products; Sermorelin → sermorelin/tesamorelin only).
//   recommendPick?(items) — optional; returns the default recommended SKU (else
//     cheapest).
//   headline?/subhead?/aboutItems? — recommendation-slide copy.
//   onComplete, answers, setLoading, clearSavedProgress, primaryGoal, currentStep,
//   timestamp, email, isProcessing — standard checkout props.

const fmtUsd = (cents) => `$${((Number(cents) || 0) / 100).toFixed(2)}`;

const DEFAULT_BENEFITS = [
  'Provider review & prescription included',
  'Free, discreet shipping',
  'Unlimited care-team messaging',
  'No long-term contract — cancel anytime',
];

const CatalogCheckoutStep = ({
  catalogVisitType,
  // §video-visit: the Beluga visitType this flow submits, when it differs from
  // the catalog it shops (WSH → mensSexualHealth catalog, Sermorelin → antiAging).
  belugaVisitType,
  // §video-visit: branded weight loss shares visitType 'weightloss'; its flow
  // tells the BE lookup apart.
  belugaFlow,
  pharmacy = 'beluga',
  filter,
  recommendPick,
  headline,
  subhead,
  aboutItems,
  benefits = DEFAULT_BENEFITS,
  trustText = 'Free expedited shipping — your treatment ships in 2–3 days once your provider prescribes it.',
  // §concierge-add-on: flows that offer Dr. Mavroson's Concierge Weight
  // Management as a page before payment (branded weight loss, TRT, metabolic).
  conciergeUpsell = false,
  // §no-partner-ship: false removes the "our partner pharmacy ships it" path —
  // only local-pickup-capable SKUs are listed and checkout is forced local
  // (own pharmacy or manufacturer pharmacy, consult fee only). Branded WL.
  allowShip = true,
  onComplete, isProcessing, answers, setLoading, clearSavedProgress, primaryGoal, currentStep, timestamp, email,
}) => {
  const stagingTest =
    typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('belugaEnv') === 'staging';

  const [meds, setMeds] = useState(null); // null = loading
  const [error, setError] = useState('');
  // §consult-fee source of truth (2026-09-04). The catalog row carries its own
  // `consultFeeCents` and this screen used to display THAT — a second copy of a
  // number the charge path resolves independently via resolveConsultFee.
  //
  // Patrick BE has since confirmed the catalog copy DOES run through the same
  // applyMarkup, so the two agree by construction and no divergence is expected.
  // We read /consult-fee anyway, for two reasons that survive that answer:
  //   1. it IS the charge path's resolver, so the displayed number is the charged
  //      number by identity rather than by two paths agreeing to compute alike;
  //   2. it is free — SquarePaymentForm on this same screen already fetches this
  //      exact response for the Square merchant config, and the request is now
  //      deduped in the api layer, so this reader adds no round trip.
  // The warning below is therefore an ASSERTION on that agreement, not a guard
  // against a suspected bug. On any failure we fall back to the catalog value
  // rather than blocking checkout.
  const [resolvedFee, setResolvedFee] = useState(null); // { feeCents, membershipCovered } | null
  // §dexa-add-on: session count the patient added, or null. Never 0 — absent and
  // "zero sessions" are different things and only one of them is a selection.
  const [dexaSessions, setDexaSessions] = useState(null);
  // §concierge-add-on: Dr. Mavroson's membership, selected or not.
  const [conciergeSel, setConciergeSel] = useState(false);
  // §checkout-steps: the checkout used to be one long scroll — product, pharmacy
  // fork, add-on, summary and card all stacked. Split so each decision gets its
  // own screen and PAYMENT IS LAST, with nothing to scroll past after the card
  // fields.
  //
  //   'configure'  what and where — treatment + pharmacy
  //   'addon'      the DEXA offer, skipped entirely when the flag is off
  //   'concierge'  the concierge-care offer, only on flows that opt in and for
  //                patients in a state the practice serves
  //   'pay'        order summary + card
  const [checkoutStep, setCheckoutStep] = useState('configure');
  const [activeMedId, setActiveMedId] = useState(null); // patient override; null = recommended
  const [modalOpen, setModalOpen] = useState(false);
  // §regular_rx fulfillment fork (dark until the catalog flips a SKU to
  // dispenseType:'regular_rx'; all SKUs are 'compounded' today):
  //   'ship'  → Fitpal's pharmacy fulfills, charge med retail (default, unchanged)
  //   'local' → doctor e-Rx's to the patient's own pharmacy; charge CONSULT FEE only
  // §pharmacy-choice carry-over: flows that ask "where should we send your
  // prescription?" upstream (branded weightloss — `pharmacy-choice` +
  // `pharmacy-search`, both BEFORE this step) must land here already on the
  // local path. Without this the patient picks "send it to my own pharmacy",
  // then checkout silently defaults back to 'ship' — billing the full med
  // retail price and promising 2–3 day shipping instead of the consult fee.
  // Flows with no such slide read undefined → 'ship', unchanged. A seeded
  // 'local' on a ship-only SKU is harmless: `effFulfillment` below re-derives
  // from what the catalog actually offers.
  const [fulfillment, setFulfillment] = useState(
    () => (['own', 'manufacturer'].includes(answers?.['pharmacy-choice']) ? 'local' : 'ship'),
  );
  // Seed the pharmacy she already chose upstream so she isn't made to search
  // for the same store a second time (and `localBlocked` doesn't gate payment).
  const [localPharmacy, setLocalPharmacy] = useState(
    () => (answers?.['pharmacy-search']?.pharmacyId ? answers['pharmacy-search'] : null),
  ); // { pharmacyId, storeName, city, state, zip, … }
  // §otp: phone is verified upstream on the contact slide (BasicContactStep);
  // the token rides the basic-contact answer to here and is relayed to payment.
  const phoneVerifyToken = answers?.['basic-contact']?.phoneVerifyToken || undefined;

  // §lead-capture: on reaching checkout we have name/email/phone/address — persist
  // the lead so the dashboard can re-target if the patient abandons before paying.
  // Fire-and-forget, flag-gated (dark until the BE /leads endpoint ships).
  useEffect(() => {
    if (!OTP_ENABLED) return;
    const c = answers?.['basic-contact'] || {};
    const s = answers?.['shipping-address'] || answers?.shipping || answers?.address || {};
    if (!c.email && !c.phone) return;
    apiService.captureLead({
      name: c.name, email: c.email, phone: c.phone,
      address1: s.address1, city: s.city, state: s.state, zip: s.zip || s.zipcode,
      visitType: catalogVisitType, flow: primaryGoal, lastStep: 'checkout',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // §consult-fee: keyed on catalogVisitType, which is the canonical Beluga
  // visitType (`weightloss`), NOT the flow name (`brandedWeightloss` → 400
  // fee_not_configured). SquarePaymentForm already keys its Square config the
  // same way, so both surfaces resolve identically.
  useEffect(() => {
    let alive = true;
    apiService.getCheckoutConfig(catalogVisitType).then((cfg) => {
      if (!alive || !cfg) return;
      const cents = Number(cfg.feeCents);
      if (!Number.isFinite(cents)) return;
      setResolvedFee({ feeCents: cents, membershipCovered: cfg.membershipCovered === true });
    });
    return () => { alive = false; };
  }, [catalogVisitType]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    apiService
      .getMedications({ visitType: catalogVisitType, pharmacy })
      .then((items) => {
        if (!alive) return;
        // Hidden products never reach the list, including via the fallback.
        let visible = (items || []).filter((i) => !isHiddenMedication(i));
        // §no-partner-ship: a SKU with no local path would fall back to ship —
        // drop it here, before the filter's empty-list fallback can re-add it.
        if (!allowShip) visible = visible.filter((i) => i.fulfillment?.local === true && i.consultFeeCents != null);
        const list = typeof filter === 'function' ? visible.filter(filter) : visible;
        setMeds(list.length ? list : visible);
      })
      .catch((e) => { console.error('catalog fetch failed', e); if (alive) setError('We could not load your treatment right now. Please try again.'); })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogVisitType, pharmacy]);

  const sorted = useMemo(
    () => (meds || []).slice().sort((a, b) => (a.price || 0) - (b.price || 0)),
    [meds],
  );
  const recommended = useMemo(() => {
    if (!sorted.length) return null;
    if (typeof recommendPick === 'function') { const p = recommendPick(sorted); if (p) return p; }
    return sorted[0]; // cheapest as the sensible default
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sorted]);
  const active = useMemo(
    () => (activeMedId && sorted.find((m) => m.medId === activeMedId)) || recommended,
    [activeMedId, sorted, recommended],
  );

  const isRegularRx = active?.dispenseType === 'regular_rx';
  // §consult-fee semantics (FE PRD membership-covered consult §1): the raw value
  // distinguishes three states — >0 = consult fee charged on local pickup;
  // 0 = consult COVERED by the $59/mo membership ($0 due today, NOT "free");
  // null = local pickup not offered for this visitType.
  const rawConsultFee = active?.consultFeeCents;
  // AVAILABILITY stays on the catalog row and only the catalog row: `null` there
  // is not a price, it is "local pickup is not offered for this SKU". Sourcing
  // availability from /consult-fee would hand a fee to a SKU that has no local
  // path and silently offer pickup that cannot be fulfilled — see canLocal below.
  //
  // The AMOUNT comes from /consult-fee once it resolves, because that is the
  // number the patient is charged. Falling back to the catalog copy on failure
  // re-opens the divergence, but a wrong-by-a-markup price beats a checkout that
  // will not render, and today the two are identical.
  const consultFeeCents = resolvedFee ? resolvedFee.feeCents : (Number(rawConsultFee) || 0);
  const consultCovered = resolvedFee
    ? (resolvedFee.feeCents === 0 || resolvedFee.membershipCovered)
    : rawConsultFee === 0;
  // §fulfillment availability is catalog-driven — each item carries explicit
  // `fulfillment: { ship, local }` (BACKEND_REGULAR_RX_FULFILLMENT_PRD). When the
  // field is absent (compounded / legacy items) default to ship-only, i.e. the
  // current behavior is unchanged.
  const fx = active?.fulfillment || null;
  const canShip = allowShip && (fx ? fx.ship === true : true);
  // rawConsultFee != null is the AVAILABILITY signal (see above), never the price.
  const canLocal = Boolean(fx && fx.local === true && rawConsultFee != null);
  // BE says these agree by construction (both run applyMarkup). Assert it rather
  // than trust it: if they ever disagree, that shared assumption has broken —
  // a stale catalog copy, or a markup rule that changed on one path only — and
  // whichever surface a screen happens to read decides whether a clinic gets paid.
  useEffect(() => {
    if (!resolvedFee || rawConsultFee == null) return;
    if (Number(rawConsultFee) !== resolvedFee.feeCents) {
      console.warn(
        `[consult-fee] catalog says ${rawConsultFee}, /consult-fee says ${resolvedFee.feeCents} ` +
        `for visitType=${catalogVisitType}. Displaying /consult-fee (the charge path's own number).`,
      );
    }
  }, [resolvedFee, rawConsultFee, catalogVisitType]);
  const showFork = canShip && canLocal; // patient chooses; else the single offered path is forced
  const effFulfillment = showFork ? fulfillment : (canLocal ? 'local' : 'ship');
  const isLocal = effFulfillment === 'local';
  // Local pickup → charge the CONSULT FEE only (patient pays their pharmacy for
  // the drug). Ship/compounded → med retail. The BE re-computes the charge
  // server-side from prescriptionFulfillment; this is display only.
  // §dexa-add-on rides on top of whatever the treatment costs, on either
  // fulfillment path — it is a separate product, not a discount or a substitute.
  // Added unconditionally (the helper returns 0 for no selection) so there is no
  // branch in which the line item can silently drop out of the total.
  const dexaCents = dexaAddOnCents(dexaSessions);
  // §concierge-add-on: the first month rides on today's charge; the backend
  // starts the monthly subscription from the card on file.
  const conciergeCents = conciergeAddOnCents(conciergeSel);
  const treatmentCents = active ? (isLocal ? consultFeeCents : (Number(active.price) || 0)) : 0;
  const totalCents = treatmentCents + dexaCents + conciergeCents;
  const localBlocked = isLocal && !localPharmacy?.pharmacyId; // can't ship an Rx to nowhere
  const selectedMedIds = active ? [active.medId] : [];
  const pharmacySelection = (isLocal && localPharmacy?.pharmacyId)
    ? { mode: 'own', pharmacyId: String(localPharmacy.pharmacyId), storeName: localPharmacy.storeName, city: localPharmacy.city, state: localPharmacy.state, zip: localPharmacy.zip }
    : undefined;
  // Best-effort seed for the pharmacy search from the shipping address (if the
  // flow collected one); the patient can still search freely.
  const shippingDefaults = (() => {
    const s = (answers && (answers['shipping-address'] || answers['shippingAddress'] || answers.shipping || answers.address)) || null;
    // `zipCode` is the key the shipping-address slide actually writes (branded
    // weightloss, TRT, …); `zip`/`zipcode` are legacy/other-flow spellings. It
    // was missing here, so the checkout pharmacy search prefilled city + state
    // but left ZIP empty while the flow's own pharmacy-search slide filled all
    // three — the inconsistency patients were hitting on the local path.
    const zip = s && (s.zip || s.zipcode || s.zipCode);
    if (s && (s.city || s.state || zip)) return { city: s.city, state: s.state, zip };
    return undefined;
  })();
  // Which manufacturer pharmacy (if any) dispenses the SELECTED sku — recomputed
  // when the patient switches dosage via "Modify your plan".
  const manufacturerPharmacy = resolveManufacturerPharmacy(active);
  // §manufacturer-ships (2026-09-04): the manufacturer pharmacy travels the LOCAL
  // envelope (mode:'own', consult fee only) because the patient pays the
  // manufacturer for the drug — but it is a mail-order pharmacy, not a counter.
  // It SHIPS to the patient's home; nobody picks anything up. Every string that
  // reads "local pickup" off `isLocal` alone was therefore wrong on this path,
  // and sat directly above a banner saying "it ships to your home".
  const mfrSelected = isLocal && isManufacturerPharmacySelected(localPharmacy, manufacturerPharmacy);
  // §manufacturer-pricing: what LillyDirect/NovoCare bill the patient directly.
  // DISPLAY ONLY — deliberately not referenced by totalCents or by the `amount`
  // passed to SquarePaymentForm below. On this route we charge the consult fee
  // and the manufacturer bills the drug; folding this in would charge a patient
  // hundreds for something we are not dispensing. null when we have no figure
  // for the selected SKU, which renders nothing rather than guessing.
  const mfrPrice = manufacturerPriceCopy(resolveManufacturerPrice(active, manufacturerPharmacy));
  // §pharmacy-choice 'manufacturer': she chose the maker's pharmacy upstream, so
  // pre-select it — and re-point it if "Modify your plan" switches maker (a
  // Zepbound Rx must not go to NovoCare). A store she searched for is left alone.
  const wantsMfr = answers?.['pharmacy-choice'] === 'manufacturer';
  useEffect(() => {
    if (!wantsMfr || !manufacturerPharmacy) return;
    setLocalPharmacy((cur) => {
      const isMfrRecord = !cur || Object.values(MANUFACTURER_PHARMACIES)
        .some((m) => String(m.pharmacy.pharmacyId) === String(cur.pharmacyId));
      return isMfrRecord ? manufacturerPharmacy.pharmacy : cur;
    });
  }, [wantsMfr, manufacturerPharmacy]);
  // The add-on step exists only while the feature is on; with the flag off the
  // patient goes straight from configure to pay and never sees a skipped screen.
  // BE #349: no DEXA step when the rest of the order is $0 (membership-covered
  // consult) — that path never reaches the Square charge.
  const hasAddOnStep = dexaOfferAvailable(totalCents - dexaCents);
  useEffect(() => { if (!hasAddOnStep && dexaSessions) setDexaSessions(null); }, [hasAddOnStep, dexaSessions]);
  const hasConciergeStep = conciergeUpsell && conciergeAddOnAvailable(answers);
  const goToPayment = () => setCheckoutStep('pay');
  const advanceFromConfigure = () => setCheckoutStep(hasAddOnStep ? 'addon' : hasConciergeStep ? 'concierge' : 'pay');
  const advanceFromAddon = () => setCheckoutStep(hasConciergeStep ? 'concierge' : 'pay');
  const stepBeforePay = hasConciergeStep ? 'concierge' : hasAddOnStep ? 'addon' : 'configure';
  // Nothing ships on the local path, so promising "Free, discreet shipping"
  // next to a $59 visit fee contradicts the option the patient just picked —
  // unless the destination is the manufacturer, which does ship.
  const displayBenefits = isLocal
    ? [
        ...benefits.filter((b) => !/shipp?ing|ships/i.test(b)),
        mfrSelected
          ? `Shipped to your door by ${manufacturerPharmacy.brand}`
          : 'Prescription sent electronically to your pharmacy',
      ]
    : benefits;
  // The manufacturer fills the prescription itself, so choosing it IS the local
  // path — it must move fulfillment across, not just set a pharmacy. Selecting it
  // while 'ship' stayed active would charge Fitpal's med retail price for a
  // prescription Fitpal never fills: the patient pays twice, once here and again
  // at LillyDirect/NovoCare.
  const selectManufacturerPharmacy = (p) => {
    setLocalPharmacy(p);
    setFulfillment('local');
  };
  // Say so before it happens. Only when the switch would actually change the
  // total — on the local path the price is already the consult fee.
  const mfrSwitchNote = (!isLocal && showFork && manufacturerPharmacy)
    ? `Choosing this moves your order to the local-pharmacy option: you'd pay ${
        consultCovered ? 'no visit fee (covered by your membership)' : fmtUsd(consultFeeCents)
      } here instead of ${fmtUsd(Number(active.price) || 0)}, and pay ${manufacturerPharmacy.brand} directly for the medication.`
    : undefined;
  // What the badge under the fork announces.
  const destination = (showFork || (canLocal && !canShip))
    ? describePharmacyDestination({ isLocal, localPharmacy, mfr: manufacturerPharmacy })
    : null;
  const chooseSku = (medId) => { setActiveMedId(medId); setModalOpen(false); };
  const submit = (payment) => onComplete({
    ...payment,
    selectedMedIds,
    selectedMedId: active?.medId,
    prescriptionFulfillment: isRegularRx ? effFulfillment : undefined,
    pharmacySelection,
    phoneVerifyToken: phoneVerifyToken || undefined,
    // §dexa-add-on: null when nothing was added, so the field is absent rather
    // than present-and-zero. FULFILMENT IS NOT BUILT — this records what was
    // paid for; turning it into scan credits needs the backend handoff in
    // docs/FE_PRD_DEXA_BUNDLE_CREDITS.md.
    dexaAddOn: dexaAddOnPayload(dexaSessions) || undefined,
    // §concierge-add-on: same rule — absent when nothing was added. Fulfilment
    // is the backend's (docs/BACKEND_CONCIERGE_WL_ADDON_PRD.md).
    conciergeAddOn: conciergeAddOnPayload(conciergeSel) || undefined,
  });

  if (error) return <p className="max-w-md mx-auto text-center text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-4" data-testid="catalog-error">{error}</p>;
  if (meds === null) return <p className="max-w-md mx-auto text-center text-gray-500 py-10" data-testid="catalog-loading">Loading your treatment…</p>;
  if (!active) return <p className="max-w-md mx-auto text-center text-gray-600 py-10" data-testid="catalog-empty">This treatment isn’t currently available. Please check back soon.</p>;

  // Portal to <body> so the overlay escapes the slide-wrapper's CSS `transform`
  // (animation fill-mode: forwards leaves a non-`none` transform, which would make
  // this position:fixed anchor to the wrapper instead of the viewport — clipping
  // the list and breaking scroll). document.body has no transformed ancestor.
  const modal = modalOpen && createPortal(
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4" data-testid="modify-plan-modal" onClick={() => setModalOpen(false)}>
      <div className="bg-white w-full sm:max-w-lg sm:rounded-3xl rounded-t-3xl max-h-[85vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div>
            <h3 className="font-semibold text-gray-900">Choose your dosage &amp; strength</h3>
            <p className="text-sm text-gray-500">Pick a different option. Your provider confirms the final choice.</p>
          </div>
          <button type="button" onClick={() => setModalOpen(false)} aria-label="Close" className="text-gray-400 hover:text-gray-600 text-2xl leading-none px-2">×</button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-3 space-y-2">
          {sorted.map((m) => {
            const sel = m.medId === active.medId;
            return (
              <button key={m.medId} type="button" onClick={() => chooseSku(m.medId)} data-testid={`sku-${m.medId}`}
                className={`w-full text-left rounded-2xl border-2 p-3 flex items-center gap-3 transition-all ${sel ? 'border-[#2d8a5f] bg-[#f0faf4]' : 'border-gray-200 hover:border-gray-300'}`}>
                <MedThumb imageUrl={m.imageUrl} alt={m.name} size="w-11 h-11" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900 leading-snug">{m.name || m.medication}</p>
                  <p className="text-xs text-gray-500">{[m.strength, m.daySupply ? `${m.daySupply}-day` : null].filter(Boolean).join(' · ')}</p>
                </div>
                {/* Without the ship path every SKU costs the same visit fee —
                    the ship price here would be a number nobody is charged. */}
                {allowShip && <span className="text-sm font-bold text-gray-900 flex-shrink-0">{fmtUsd(m.price)}</span>}
              </button>
            );
          })}
        </div>
      </div>
    </div>,
    document.body,
  );

  const gateProduct = {
    name: active.name || active.medication,
    sub: active.strength,
    // Local-only meds have no Fitpal ship price → headline the consult fee
    // (or "covered" when the membership includes it — never "$0.00").
    price: (canLocal && !canShip)
      ? (consultCovered ? 'Included with membership' : fmtUsd(consultFeeCents))
      : fmtUsd(Number(active.price) || 0),
    imageUrl: active.imageUrl,
  };

  return (
    <>
    {/* Modal rendered at top level so it's mounted in BOTH the recommend and pay
        phases — "See other dosages & strengths" opens it from the recommend slide. */}
    {modal}
    <RecommendationGate
      product={gateProduct}
      learnMoreProduct={active}
      headline={headline}
      subhead={subhead}
      aboutItems={aboutItems}
      onSeeOptions={() => setModalOpen(true)}
      seeOptionsLabel="See other dosages & strengths"
    >
      <div data-testid="catalog-checkout" className="max-w-md mx-auto">
        <div className="mb-5">
          <p className="text-[#2d8a5f] font-semibold text-sm">You're in!</p>
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">Last step before getting real results.</h2>
        </div>

        <div className="rounded-3xl border border-[#2d8a5f]/30 bg-[#f0faf4] p-5 mb-3">
          <div className="flex items-center gap-4 pb-4 border-b border-[#2d8a5f]/15">
            <MedThumb imageUrl={active.imageUrl} alt={active.name} size="w-16 h-16" />
            <div className="min-w-0 flex-1">
              <p className="font-bold text-gray-900 leading-snug" data-testid="primary-name">{active.name || active.medication}</p>
              <p className="text-sm text-gray-500">{[active.strength, active.daySupply ? `${active.daySupply}-day supply` : null].filter(Boolean).join(' · ')}</p>
              <ProductLearnMore product={active} className="mt-0.5 inline-block" />
            </div>
            <span className={`flex-shrink-0 ${isLocal && consultCovered ? 'text-sm font-medium text-[#2d8a5f] text-right' : 'font-bold text-gray-900'}`}>
              {isLocal ? (consultCovered ? 'Included with your membership' : fmtUsd(consultFeeCents)) : fmtUsd(Number(active.price) || 0)}
            </span>
          </div>
          <BenefitList items={displayBenefits} className="pt-4" />
          <button type="button" onClick={() => setModalOpen(true)} data-testid="modify-plan" className="mt-3 text-sm text-[#2d8a5f] underline underline-offset-2 hover:text-[#236b4a]">
            Modify your plan
          </button>
          <ProviderReviewNotice className="mt-3" />
        </div>

        {checkoutStep === 'configure' && (
        <>
        {/* §fulfillment — catalog-driven. Rendered only when a local option is
            offered: the fork (ship + local) when both are available, or a
            local-only layout when the med isn't Fitpal-shippable. Ship-only meds
            (compounded / legacy — all catalog SKUs today) render nothing here. */}
        {(showFork || (canLocal && !canShip)) && (
          <div className="rounded-2xl border border-gray-200 p-5 mb-4" data-testid="fulfillment-fork">
            {showFork ? (
              <>
                <h3 className="font-semibold text-gray-900 mb-1">Where would you like your prescription sent?</h3>
                <p className="text-sm text-gray-500 mb-4">Brand-name medications can be filled by our partner pharmacy or a local pharmacy of your choice.</p>
                <div className="space-y-2">
                  <button type="button" data-testid="fulfillment-ship" onClick={() => setFulfillment('ship')}
                    className={`w-full text-left rounded-xl border-2 p-4 transition-all ${!isLocal ? 'border-[#2d8a5f] bg-[#f0faf4]' : 'border-gray-200 hover:border-gray-300'}`}>
                    <div className="flex justify-between items-start gap-3">
                      {/* Tenant-neutral on purpose: this component serves every
                          tenant (DexaSlim et al.), so naming Fitpal here was
                          wrong on any non-Fitpal intake. */}
                      <span className="font-medium text-gray-900 leading-snug">Use our partner pharmacy — ships discreetly to my door</span>
                      <span className="font-bold text-gray-900 flex-shrink-0">{fmtUsd(Number(active.price) || 0)}</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">We fulfill and ship it to you — price includes the medication.</p>
                  </button>
                  <button type="button" data-testid="fulfillment-local" onClick={() => setFulfillment('local')}
                    className={`w-full text-left rounded-xl border-2 p-4 transition-all ${isLocal ? 'border-[#2d8a5f] bg-[#f0faf4]' : 'border-gray-200 hover:border-gray-300'}`}>
                    <div className="flex justify-between items-start gap-3">
                      <span className="font-medium text-gray-900 leading-snug">
                        {mfrSelected
                          ? `Send it to ${manufacturerPharmacy.brand} — ships to my door`
                          : 'Send it to my own pharmacy — I will pick it up locally'}
                      </span>
                      {/* covered = "Included with your membership", never "$0.00"/"Free" */}
                      <span className={`flex-shrink-0 ${consultCovered ? 'text-sm font-medium text-[#2d8a5f] text-right' : 'font-bold text-gray-900'}`} data-testid="local-price-label">
                        {consultCovered ? 'Included with your membership' : fmtUsd(consultFeeCents)}
                      </span>
                    </div>
                    {/* The manufacturer's own price, kept visually separate from
                        the number above it — that one is what we charge, this one
                        is what someone else charges. Merging them would read as a
                        total. */}
                    {mfrSelected && mfrPrice && (
                      <div className="mt-2 flex items-baseline justify-between gap-3 rounded-lg bg-white/70 border border-gray-200 px-2.5 py-1.5" data-testid="mfr-price-fork">
                        <span className="text-xs text-gray-600">{mfrPrice.label}</span>
                        {mfrPrice.amount && (
                          <span className="text-sm font-semibold text-gray-900 flex-shrink-0" data-testid="mfr-price-fork-amount">
                            {mfrPrice.amount}
                          </span>
                        )}
                      </div>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      {mfrSelected
                        ? `Your provider sends the prescription to ${manufacturerPharmacy.pharmacy.storeName} and it ships to your home. You pay only the visit fee here — ${manufacturerPharmacy.brand} bills you for the medication.`
                        : consultCovered
                          ? 'Your provider consult is included in your membership — no charge from Fitpal today. Use your insurance at the pharmacy (many patients pay $0–$25).'
                          : 'Your provider sends the prescription to your pharmacy. You pay only the visit fee here — use your insurance at the pharmacy (many patients pay $0–$25).'}
                    </p>
                  </button>
                </div>
              </>
            ) : (
              <>
                <h3 className="font-semibold text-gray-900 mb-1">Fill at your local pharmacy</h3>
                <p className="text-sm text-gray-500 mb-4">
                  {consultCovered
                    ? 'Your provider consult is included with your membership — no charge from Fitpal today. Use your insurance at the pharmacy (many patients pay $0–$25).'
                    : `Your provider sends this prescription to your pharmacy. You pay only the ${fmtUsd(consultFeeCents)} visit fee here — use your insurance at the pharmacy (many patients pay $0–$25).`}
                </p>
              </>
            )}
            {/* §destination-badge — name the destination in one line, right under
                the choice that sets it. Without this the patient had to infer it
                from which card was outlined green, and the 'pending' state (own
                pharmacy chosen, no store picked) was visible only as a small
                amber line further down, after the search box. */}
            {destination && (
              <div
                className={`mt-4 flex items-start gap-2 rounded-xl px-3 py-2 ${
                  destination.kind === PHARMACY_DESTINATION.PENDING
                    ? 'bg-amber-50 border border-amber-200'
                    : 'bg-[#f0faf4] border border-[#2d8a5f]/25'
                }`}
                data-testid="pharmacy-destination-badge"
                data-destination={destination.kind}
              >
                <span
                  className={`mt-0.5 w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                    destination.kind === PHARMACY_DESTINATION.PENDING ? 'bg-amber-500' : 'bg-[#2d8a5f]'
                  }`}
                />
                <div className="min-w-0">
                  <p
                    className={`text-xs font-semibold uppercase tracking-wide ${
                      destination.kind === PHARMACY_DESTINATION.PENDING ? 'text-amber-700' : 'text-[#2d8a5f]'
                    }`}
                  >
                    {destination.kind === PHARMACY_DESTINATION.MANUFACTURER
                      ? 'Manufacturer pharmacy'
                      : 'Sending to'}
                  </p>
                  <p className="text-sm font-semibold text-gray-900 leading-snug" data-testid="pharmacy-destination-label">
                    {destination.label}
                  </p>
                  <p className="text-xs text-gray-500">{destination.detail}</p>
                </div>
              </div>
            )}

            {/* §manufacturer-pharmacy: for a branded SKU whose maker runs its own
                direct pharmacy, offer it as a highlighted one-click choice.
                Rendered on EITHER branch of the fork (2026-09-04) — gating it on
                isLocal hid it from everyone who left the default selected. It
                still never replaces the search below. */}
            {manufacturerPharmacy && canLocal && (
              <div className={showFork ? 'mt-4 pt-4 border-t border-gray-100' : 'mt-2'}>
                <ManufacturerPharmacyBanner
                  mfr={manufacturerPharmacy}
                  selected={isLocal ? localPharmacy : null}
                  onSelect={selectManufacturerPharmacy}
                  onClear={() => setLocalPharmacy(null)}
                  note={mfrSwitchNote}
                  price={mfrPrice}
                  productName={active.name || active.medication}
                />
              </div>
            )}

            {isLocal && (
              <div className={showFork || manufacturerPharmacy ? 'mt-4 pt-4 border-t border-gray-100' : 'mt-2'}>
                <PharmacySelectStep
                  question={{ id: 'rx-local-pharmacy', question: 'Find your pharmacy', subtitle: 'Search by name, city, or ZIP code.' }}
                  value={localPharmacy}
                  onAnswer={setLocalPharmacy}
                  onNext={() => {}}
                  searchDefaults={shippingDefaults}
                  hideContinue
                />
                {localBlocked && (
                  <p className="text-xs text-amber-700 mt-2" data-testid="local-pharmacy-required">Select your pharmacy to continue.</p>
                )}
              </div>
            )}
          </div>
        )}

        {/* §pharmacy-choice honesty gate: she asked upstream for a local
            pharmacy, but this SKU isn't dispensable that way (compounded, or
            the catalog hasn't seeded fulfillment.local + consultFeeCents for
            it). Say so explicitly — silently charging med retail after she
            chose local pickup is what made this look like a billing bug. */}
        {answers?.['pharmacy-choice'] === 'own' && !canLocal && (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 mb-4" data-testid="local-unavailable-notice">
            <p className="text-sm text-amber-900">
              <span className="font-semibold">Heads up —</span> you asked us to send this prescription to your own
              pharmacy, but{' '}
              <span className="font-medium">{active.name || active.medication}</span> is only available shipped from
              our pharmacy. The price below includes the medication. Choose{' '}
              <button type="button" onClick={() => setModalOpen(true)} className="underline underline-offset-2 font-medium">
                a different option
              </button>{' '}
              if you'd rather fill it locally.
            </p>
          </div>
        )}

        <TrustBanner>
          {mfrSelected
            ? `Your provider e-prescribes to ${manufacturerPharmacy.pharmacy.storeName}, which ships it to your home. You pay Fitpal only the visit fee.`
            : isLocal
              ? 'Your provider e-prescribes to your pharmacy — no shipping. You pay Fitpal only the visit fee.'
              : trustText}
        </TrustBanner>

        {/* Continue is blocked for the same reason payment was: an Rx cannot be
            sent to a pharmacy the patient never chose. */}
        {localBlocked && (
          <p className="text-sm text-amber-700 mb-2" data-testid="configure-blocked">
            Select your pharmacy above to continue.
          </p>
        )}
        <button
          type="button"
          onClick={advanceFromConfigure}
          disabled={localBlocked}
          data-testid="checkout-continue"
          className="w-full rounded-2xl bg-[#2d8a5f] px-4 py-4 font-semibold text-white hover:bg-[#246f4d] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Continue
        </button>
        </>
        )}

        {/* §dexa-add-on — 1 scan / 3-scan bundle (data/dexaAddOn.js). Shared
            page with the compound weight-loss checkout; see CheckoutUpsells. */}
        {checkoutStep === 'addon' && (
          <DexaUpsell sessions={dexaSessions} setSessions={setDexaSessions} onContinue={advanceFromAddon} onBack={() => setCheckoutStep('configure')} />
        )}

        {/* §concierge-add-on — Dr. Mavroson's Concierge Weight Management,
            offered right before payment on the flows that opt in. */}
        {checkoutStep === 'concierge' && (
          <ConciergeUpsell selected={conciergeSel} setSelected={setConciergeSel} onContinue={goToPayment} onBack={() => setCheckoutStep(hasAddOnStep ? 'addon' : 'configure')} />
        )}

        {checkoutStep === 'pay' && (
        <>
        <div className="rounded-2xl border border-gray-200 p-5 mb-4">
          <div className="flex justify-between text-sm mb-2">
            <span className="text-gray-600">
              {isLocal
                ? (consultCovered
                    ? 'Provider consult'
                    : mfrSelected
                      ? `Visit fee (medication billed by ${manufacturerPharmacy.brand})`
                      : 'Visit fee (medication billed at your pharmacy)')
                : (active.name || active.medication)}
            </span>
            <span className="font-medium">
              {isLocal && consultCovered ? <span className="text-[#2d8a5f]">Included with your membership</span> : fmtUsd(treatmentCents)}
            </span>
          </div>
          {/* Add-ons get their own lines. Folding them into the treatment line
              would make the patient unable to see what each cost them. */}
          <AddOnLineItems dexaSessions={dexaSessions} dexaCents={dexaCents} conciergeCents={conciergeCents} />
          <MembershipNotice coversConsult={isLocal && consultCovered} />
          <div className="border-t border-gray-200 mt-2 pt-3 flex justify-between items-center">
            <span className="font-semibold text-lg">{isLocal && consultCovered ? 'Due today' : 'Total'}</span>
            <span className="text-2xl font-bold text-[#2d8a5f]" data-testid="cart-total">{fmtUsd(totalCents)}</span>
          </div>
        </div>

        <div className="rounded-2xl border border-gray-200 p-5">
          <h3 className="font-semibold text-gray-900 mb-4">Payment details</h3>
          {/* §otp: phone was verified on the contact slide; phoneVerifyToken
              (from the basic-contact answer) is relayed to the payment below. */}
          {localBlocked ? (
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="payment-blocked-local">
              Select your pharmacy above to continue to payment.
            </p>
          ) : stagingTest ? (
            <div className="space-y-3">
              <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                ⚠️ Staging sign-off mode — Square is bypassed. Submits a test visit to Beluga <strong>staging</strong> with no charge.
              </p>
              <button type="button" data-testid="staging-bypass-pay" disabled={isProcessing} onClick={() => submit({ paymentId: 'STAGING_TEST_BYPASS' })}
                className="w-full rounded-xl bg-[#2d8a5f] px-4 py-3 font-semibold text-white disabled:opacity-50">
                {isProcessing ? 'Submitting…' : `[Staging test] Skip payment & submit (${fmtUsd(totalCents)})`}
              </button>
            </div>
          ) : (
            <>
            {/* §concierge-oto: tell the patient the card will be saved BEFORE it is
                (renders only when the one-click offer's card-on-file path is on). */}
            <OtoCardSaveNotice answers={answers} visitType={catalogVisitType} />
            <SquarePaymentForm modalityVisitType={belugaVisitType || catalogVisitType} modalityFlow={belugaFlow} planId={null} selectedMedIds={selectedMedIds} prescriptionFulfillment={isRegularRx ? effFulfillment : undefined} phoneVerifyToken={phoneVerifyToken} amount={totalCents} onComplete={submit} consultFeeVisitType={catalogVisitType}
              // §concierge-oto: store the card (verifyBuyer → BE CreateCard) whenever
              // the post-payment one-click offer could follow (flag on + NY), so the
              // offer page can charge it without a second card entry.
              cardOnFile={conciergeSel || otoCardOnFile(answers, catalogVisitType)}
              // BE #348: the backend keeps the card for the offer only when told so, under
              // the SAME condition that renders the notice above.
              otoCardOnFile={otoCardOnFile(answers, catalogVisitType)}
              dexaAddOn={hasAddOnStep ? dexaAddOnPayload(dexaSessions) : null} nonDiscountableCents={hasAddOnStep ? dexaCents : 0} answers={answers} setLoading={setLoading} clearSavedProgress={clearSavedProgress} primaryGoal={primaryGoal}
              currentStep={currentStep} timestamp={timestamp} email={email} />
            </>
          )}
        </div>
        <button
          type="button"
          onClick={() => setCheckoutStep(stepBeforePay)}
          data-testid="pay-back"
          className="w-full mt-3 text-sm text-gray-500 underline underline-offset-2 hover:text-gray-700"
        >
          Back
        </button>
        </>
        )}
      </div>
    </RecommendationGate>
    </>
  );
};

export default CatalogCheckoutStep;
